/**
 * หน้าจอ RBAC Admin — รูปร่างข้อมูลตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "รูปร่างที่ freeze แล้ว — อ่าน org + แก้ permission matrix
 * (`WO-P1-M2-011`)" และ "— สร้าง master + มอบ/ถอด role + อ่าน audit (`WO-P1-M2-012`)")
 *
 * ไฟล์นี้เป็นภาพสะท้อนของเอกสารนั้นเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง แนวเดียว
 * กับ `lib/goodsReceipts/types.ts`: ฟิลด์ที่ contract บังคับถูกบังคับที่ชนิดข้อมูล
 * ไม่ปล่อยเป็น nullable เพื่อกันความไม่แน่นอนที่ไม่มีอยู่
 *
 * **`plant_code` ปรากฏที่นี่ได้ทางเดียว** — เป็น**ผลลัพธ์**ที่บอกว่าพนักงานคนหนึ่ง
 * ถูกให้สิทธิ์เข้าถึงโรงงานใดบ้าง (`Employee.plant_codes`,
 * `SetEmployeePlantAccessBody.plant_code`) ไม่ใช่**ตัวกรอง**ของคำขอ: endpoint กลุ่มนี้
 * ยังเป็นระดับ System Administration ที่จัดการทั้งองค์กรข้ามทุกโรงงาน และไม่รับ
 * `plant_code` เป็นขอบเขตของคำขอเลย (`docs/API_CONTRACT.md`, `admin_schemas.py`
 * — "No ``plant_code``, anywhere")
 *
 * `WO-P1-M10-018` เติมส่วนที่สองไฟล์แรกเว้นไว้จนถึงวันที่มีหน้าจอของมันจริง:
 * `Employee`, การสร้าง department/employee (`WO-P1-M2-012`) และการผูกพนักงานเข้ากับ
 * แผนก/โรงงาน (`WO-P1-M2-013`)
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — หลัง D-060 มีเพียง `ADMIN` ที่ถือมันได้ */
export const RBAC_ADMIN_MODULE = 'RBAC_ADMIN';

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `422` เมื่อพยายามให้สิทธิ์ module กลุ่ม System Administration ผ่าน matrix (D-060)
 *
 * **ควรเป็นกรณีที่เอื้อมไม่ถึงจากหน้าจอนี้** เพราะช่องของ module กลุ่มนั้นถูก
 * disable และไม่มีเส้นทางใดประกอบ `POST` ให้มันได้ ถ้าผู้ใช้เห็นข้อความนี้จริง
 * แปลว่า **หน้าจอกับ API ไม่ตรงกันแล้ว** ซึ่งเป็น bug ที่ต้องเห็น ไม่ใช่ความล้มเหลว
 * ที่กลืนเงียบ ๆ ให้ดูเหมือนบันทึกสำเร็จ
 */
export const SYSTEM_ADMIN_MODULE_NOT_GRANTABLE = 'SYSTEM_ADMIN_MODULE_NOT_GRANTABLE';

/** `422` เมื่อ `module_code` ที่ส่งไปไม่อยู่ในแคตาล็อก `module` */
export const MODULE_NOT_FOUND = 'MODULE_NOT_FOUND';

/**
 * กฎทุกข้อที่ contract เขียนไว้ตรง ๆ สำหรับ `POST /v1/admin/permissions`
 *
 * **ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุ** — รหัสที่หน้าจอคิดขึ้นเองคือข้อความที่
 * ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
export const PERMISSION_MATRIX_RULES = [
  SYSTEM_ADMIN_MODULE_NOT_GRANTABLE,
  MODULE_NOT_FOUND,
] as const;

export type PermissionMatrixRule = (typeof PERMISSION_MATRIX_RULES)[number];

/* -------------------------------------------------------------- resource -- */

/** หนึ่งแผนกจาก `GET /v1/admin/departments` — master list ล้วน ไม่มี matrix ติดมา */
export interface Department {
  readonly department_id: string;
  readonly department_name: string;
  /** มี offset ของโรงงานติดมาเสมอ (`+07:00`, D-055) ไม่ใช่ `Z` */
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/admin/departments` — envelope ตามกติกาข้ามกลุ่ม ไม่ใช่ array เปล่า */
export interface DepartmentListPage {
  readonly items: readonly Department[];
  readonly next_cursor: string | null;
}

/** สี่ flag ของ RBAC สองมิติ (D-008) — **อิสระต่อกันทั้งสี่ตัว** */
export interface PermissionFlags {
  readonly can_create: boolean;
  readonly can_view: boolean;
  readonly can_edit: boolean;
  readonly can_approve: boolean;
}

/**
 * หนึ่งแถวของ matrix — หนึ่ง module ของแผนกที่กำลังดูอยู่
 *
 * contract ระบุว่า `GET` **แจกแจง module ทั้งแคตาล็อก** ไม่ใช่เฉพาะแถวที่มีใน
 * `department_module_permission`: module ที่ยังไม่เคยถูกตั้งค่าคืน flag false ทั้งสี่
 * (deny-by-default) หน้าจอจึงแสดงทั้ง grid ไม่ใช่เฉพาะช่องที่เคยติ๊ก — grid ที่ซ่อน
 * แถวที่ยังไม่เคยตั้งค่า คือ grid ที่ผู้ดูแลมองไม่เห็นสิ่งที่ตัวเองยังไม่ได้ให้
 *
 * `is_system_admin_module` เป็นธงที่ **เซิร์ฟเวอร์เป็นเจ้าของ** — หน้าจออ่านมันเพื่อ
 * ปิดช่องติ๊ก ไม่ใช่ hardcode สามรหัสไว้เอง เพราะรายการที่ frontend จำเองจะ drift
 * ออกจาก `SYSTEM_ADMIN_MODULE_CODES` และ drift คือศัตรูตัวจริงของ D-060
 */
export interface ModulePermissionRow extends PermissionFlags {
  readonly module_code: string;
  readonly module_name: string;
  readonly is_system_admin_module: boolean;
}

/** `GET /v1/admin/permissions?department_id=` — matrix ของแผนกเดียว */
export interface DepartmentPermissionMatrix {
  readonly department_id: string;
  readonly department_name: string;
  readonly modules: readonly ModulePermissionRow[];
}

/**
 * Body ของ `POST /v1/admin/permissions` — หกคีย์ ไม่มีคีย์ที่เจ็ด (`extra = "forbid"`)
 *
 * การบันทึกหนึ่งแถวส่ง **ครบทั้งสี่ flag** เสมอ เพราะ endpoint นี้ upsert ทั้งแถว
 * (แทนที่ทั้งสี่ค่า) การส่งเฉพาะ flag ที่เพิ่งเปลี่ยนจะลบอีกสามค่าทิ้งเงียบ ๆ
 */
export interface SetModulePermissionBody extends PermissionFlags {
  readonly department_id: string;
  readonly module_code: string;
}

/** คำตอบของ `POST` — แถวที่ถูกเก็บจริง ไม่ใช่ echo ของสิ่งที่ส่งไป */
export interface StoredModulePermission extends PermissionFlags {
  readonly department_id: string;
  readonly module_code: string;
}

/** สี่ action ของ matrix ตามลำดับที่หน้าจอแสดง — ใช้ร่วมกันระหว่าง view กับเทสต์ */
export const PERMISSION_FLAG_KEYS = [
  'can_create',
  'can_view',
  'can_edit',
  'can_approve',
] as const;

export type PermissionFlagKey = (typeof PERMISSION_FLAG_KEYS)[number];

/* ------------------------------------------------ บทบาทระบบ (system role) --- */

/**
 * บทบาทระบบเดียวที่ `POST /v1/admin/roles` รับ — `ADMIN` และ `AUDITOR`
 *
 * ชุดจริงอยู่ที่ `SYSTEM_ROLE_CODES` ฝั่ง backend ซึ่งเป็นผู้ตัดสิน (`422`
 * `ROLE_CODE_NOT_RECOGNISED` เมื่อไม่ตรง) ที่นี่มีไว้เพื่อ **ให้ตัวเลือกบนหน้าจอ**
 * เท่านั้น ไม่ใช่เพื่อกรองแทนเซิร์ฟเวอร์ — และด้วยเหตุนั้น หน้าจอจึงยังต้องมี
 * ข้อความของ `ROLE_CODE_NOT_RECOGNISED` ไว้จริง ๆ สำหรับวันที่สองชุดนี้เลื่อนออก
 * จากกัน
 */
export const ADMIN_ROLE_CODE = 'ADMIN';
export const AUDITOR_ROLE_CODE = 'AUDITOR';

export const SYSTEM_ROLE_CODES = [ADMIN_ROLE_CODE, AUDITOR_ROLE_CODE] as const;

export type SystemRoleCode = (typeof SYSTEM_ROLE_CODES)[number];

/* --------------------------------------------- กฎธุรกิจของ `POST /roles` --- */

/**
 * `422` เมื่อถอด `ADMIN` ออกจาก **คนสุดท้าย** ที่ยัง `ACTIVE` และถืออยู่
 *
 * **นี่คือการ์ดที่กันไม่ให้ผู้ดูแลล็อกทุกคนออกจากระบบของตัวเอง** ระบบที่ไม่เหลือ
 * ADMIN คือระบบที่ไม่มีใครแก้สิทธิ์ได้อีก และไม่มีเส้นทางกู้คืนผ่าน UI เลย
 * หน้าจอจึงต้องอธิบายการปฏิเสธนี้ให้ชัดเป็นข้อความของมันเอง ไม่ใช่ "ทำรายการ
 * ไม่สำเร็จ" ก้อนเดียวกับทุกกรณี — ผู้ดูแลที่อ่านไม่ออกว่าทำไมจะลองกดซ้ำ
 */
export const LAST_ADMIN_CANNOT_BE_REMOVED = 'LAST_ADMIN_CANNOT_BE_REMOVED';

/** `422` เมื่อ `role_code` ที่ส่งไปไม่อยู่ใน `SYSTEM_ROLE_CODES` ของเซิร์ฟเวอร์ */
export const ROLE_CODE_NOT_RECOGNISED = 'ROLE_CODE_NOT_RECOGNISED';

/**
 * กฎทุกข้อที่ contract เขียนไว้ตรง ๆ สำหรับ `POST /v1/admin/roles`
 *
 * **ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุ** ด้วยเหตุผลเดียวกับ
 * `PERMISSION_MATRIX_RULES` — รหัสที่หน้าจอคิดขึ้นเองคือคำสัญญาที่ backend
 * ไม่เคยให้ไว้
 */
export const SYSTEM_ROLE_RULES = [
  LAST_ADMIN_CANNOT_BE_REMOVED,
  ROLE_CODE_NOT_RECOGNISED,
] as const;

export type SystemRoleRule = (typeof SYSTEM_ROLE_RULES)[number];

/* ------------------------------------------------------ resource: roles --- */

/**
 * หนึ่งแถวของ `GET /v1/admin/roles` — **แถวละ role ไม่ใช่แถวละคน**
 *
 * contract ระบุตรง ๆ ว่าคนหนึ่งถือได้หลาย role และจะปรากฏหลายแถว การยุบให้เหลือ
 * แถวเดียวต่อคนที่ parser จะทำให้กรณีที่ผู้ตรวจสอบมองหาที่สุด (คนเดียวถือทั้ง
 * `ADMIN` และ `AUDITOR`) อ่านเหมือนการมอบ role เดียว — การจัดกลุ่มเพื่อแสดงผล
 * เป็นเรื่องของหน้าจอ ไม่ใช่ของ parser
 */
export interface RoleAssignment {
  readonly employee_id: string;
  readonly display_name: string;
  readonly role_code: string;
  /** มี offset ของโรงงานติดมาเสมอ (`+07:00`, D-055) ไม่ใช่ `Z` */
  readonly created_at: string;
}

/** `GET /v1/admin/roles` — envelope ตามกติกาข้ามกลุ่ม ไม่ใช่ array เปล่า */
export interface RoleAssignmentListPage {
  readonly items: readonly RoleAssignment[];
  readonly next_cursor: string | null;
}

/**
 * Body ของ `POST /v1/admin/roles` — สามคีย์ ไม่มีคีย์ที่สี่ (`extra = "forbid"`)
 *
 * `granted` **ไม่มีค่าเริ่มต้น** ทั้งที่ contract และที่นี่: body ที่ลืมระบุจะถูก
 * เดาไปทางใดทางหนึ่ง และหนึ่งในสองทางนั้นคือการถอนสิทธิ์ของผู้ดูแล
 */
export interface SetSystemRoleBody {
  readonly employee_id: string;
  readonly role_code: string;
  readonly granted: boolean;
}

/**
 * คำตอบของ `POST /v1/admin/roles` — **สถานะหลังเปลี่ยน ไม่ใช่ delta**
 *
 * นี่คือสิ่งเดียวที่หน้าจอเชื่อได้ว่า "ตอนนี้คนนี้ถืออะไรอยู่" การเดาจากปุ่มที่
 * เพิ่งกด (แล้วลบ badge ทิ้งทันที) จะแสดงผลของสิ่งที่เซิร์ฟเวอร์อาจปฏิเสธไปแล้ว —
 * ซึ่งบนหน้าจอนี้แปลว่าโชว์ว่าถอด ADMIN สำเร็จทั้งที่การ์ดกัน lockout ปฏิเสธ
 */
export interface EmployeeSystemRoles {
  readonly employee_id: string;
  readonly system_roles: readonly string[];
}

/* -------------------------------------------------- resource: audit log --- */

/**
 * `before_json`/`after_json` — snapshot ที่ผู้เขียนแต่ละครั้งเป็นคนกำหนดรูป
 *
 * contract ระบุว่าค่าถูกส่งผ่านมา **ตามที่เก็บ** ไม่ถูก normalise ให้เป็นรูป
 * เดียวกัน (สี่ flag สำหรับการแก้ matrix, รายการ role สำหรับการเปลี่ยน role,
 * แถวที่เพิ่งสร้างสำหรับการสร้าง) หน้าจอจึงต้องปฏิบัติกับมันเป็นก้อน JSON
 * ที่อ่านออกได้แต่ไม่มีสัญญาเรื่องคีย์ — การประกาศ interface ให้มันคือการที่
 * หน้าจอตัดสินแทนว่าการเปลี่ยนแปลงในอดีตแปลว่าอะไร ซึ่ง audit trail ต้องไม่ทำ
 */
export type AuditSnapshot = Readonly<Record<string, unknown>>;

/**
 * หนึ่งแถวของ `rbac_audit_log` — การเปลี่ยน "ใครทำอะไรได้" หนึ่งครั้ง
 *
 * **เป็นร่องรอยของการเปลี่ยนสิทธิ์เท่านั้น ไม่ใช่ audit ของข้อมูลธุรกิจ**
 * (`audit_log` เป็นคนละตารางที่อ่านผ่าน endpoint ของแต่ละโดเมน) — การเทสองสายนี้
 * รวมกันจะกลบสายที่เล็กกว่าและอ่อนไหวกว่ามาก
 *
 * ฟิลด์เป้าหมายเป็น `null` ได้ตามชนิดของการเปลี่ยนแปลง: การเปลี่ยน role ไม่มี
 * `module_code`, การแก้ matrix ไม่มี `target_employee_id` และ `before_json` เป็น
 * `null` เมื่อสิ่งนั้นยังไม่เคยมีอยู่ — ทั้งหมดเป็น **ข้อเท็จจริง** ไม่ใช่ข้อมูลที่หายไป
 */
export interface RbacAuditEntry {
  readonly audit_id: string;
  readonly actor_employee_id: string;
  readonly target_employee_id: string | null;
  readonly target_department_id: string | null;
  readonly module_code: string | null;
  readonly action: string;
  readonly before_json: AuditSnapshot | null;
  readonly after_json: AuditSnapshot | null;
  /** มี offset ของโรงงานติดมาเสมอ (`+07:00`, D-055) ไม่ใช่ `Z` */
  readonly created_at: string;
}

/** `GET /v1/admin/audit-log` — envelope เดิม เรียงใหม่สุดก่อน */
export interface RbacAuditLogPage {
  readonly items: readonly RbacAuditEntry[];
  readonly next_cursor: string | null;
}

/* ------------------------------------------ resource: employee (org view) --- */

/**
 * สถานะของพนักงานตาม `employee.status` — สองค่าเท่านั้น
 *
 * `INACTIVE` **ไม่ใช่การลบ**: local master ไม่เคยถูกลบจริง เพราะการอ้างถึงผู้กระทำ
 * ในอดีต (Operation Confirmation, การอนุมัติ, audit log) ต้องยังอ่านออกได้เสมอ
 * (`references/hr-integration.md`) — และการนับ "ADMIN คนสุดท้าย" ก็นับเฉพาะคนที่
 * `ACTIVE` ด้วยเหตุผลเดียวกัน
 */
export const ACTIVE_EMPLOYEE_STATUS = 'ACTIVE';
export const INACTIVE_EMPLOYEE_STATUS = 'INACTIVE';

export const EMPLOYEE_STATUSES = [
  ACTIVE_EMPLOYEE_STATUS,
  INACTIVE_EMPLOYEE_STATUS,
] as const;

export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

/**
 * หนึ่งพนักงานจาก `GET /v1/admin/employees` — ภาพรวมองค์กรของคนคนหนึ่ง
 *
 * `primary_department_id`/`_name` เป็น `null` ได้ตาม contract และ **ไม่ใช่ข้อมูลที่
 * หายไป**: ADMIN/AUDITOR ไม่จำเป็นต้องสังกัดแผนกใดเลย (D-009) เช่นเดียวกับที่
 * `system_roles` และ `plant_codes` เป็น array ว่างได้
 *
 * ⚠️ **รายการนี้บอกเฉพาะแผนก *หลัก* ไม่ใช่แผนกทั้งหมดที่คนนั้นสังกัด** และ
 * `plant_codes` **ไม่ได้บอกว่าโรงงานใดเป็นโรงงานหลัก** — สองอย่างนั้นมาจากคำตอบของ
 * `POST /admin/employees/{id}/departments` และ `.../plant-access` เท่านั้น
 * (`EmployeeDepartments`/`EmployeePlantAccess` ด้านล่าง) หน้าจอจึงต้องไม่แต่งธง
 * `is_primary`/`is_home_plant` ขึ้นมาจากรายการนี้เอง
 */
export interface Employee {
  readonly employee_id: string;
  readonly display_name: string;
  /** ค่าจริงมาจากเซิร์ฟเวอร์ จึงเป็น `string` ไม่ใช่ union — ค่าที่สามคือของเซิร์ฟเวอร์ */
  readonly status: string;
  readonly competency_flag: boolean;
  readonly primary_department_id: string | null;
  readonly primary_department_name: string | null;
  readonly system_roles: readonly string[];
  readonly plant_codes: readonly string[];
  /** อีเมลสำหรับ SSO (D-155); ไม่มีฟิลด์ในคำตอบ = ไม่ทราบ ถือเป็น `null` (สมมติฐาน WO-P2-M13-006b) */
  readonly email?: string | null;
  /** บัญชีล็อกอิน: `null` เมื่อยังไม่เคยออกให้ (`parseEmployee` เติมให้เสมอ) */
  readonly username?: string | null;
  /** `NONE` | `ACTIVE` | `MUST_CHANGE_PASSWORD` | `LOCKED` | `REVOKED` */
  readonly login_status?: string;
  /** มี offset ของโรงงานติดมาเสมอ (`+07:00`, D-055) ไม่ใช่ `Z` */
  readonly created_at: string;
  readonly updated_at: string;
}

/** คำตอบของการออกบัญชีล็อกอินหรือรีเซ็ตรหัส — รหัสชั่วคราวมาครั้งเดียว ห้ามเก็บลง storage, URL หรือ log */
export interface IssuedLogin {
  readonly employee_id: string;
  /** `null` ในคำตอบของการรีเซ็ต ซึ่งไม่ได้เปลี่ยน username */
  readonly username: string | null;
  readonly temporary_password: string;
}

/** `GET /v1/admin/employees` — envelope ตามกติกาข้ามกลุ่ม ไม่ใช่ array เปล่า */
export interface EmployeeListPage {
  readonly items: readonly Employee[];
  readonly next_cursor: string | null;
}

/* ---------------------------------------- การสร้าง master (WO-P1-M2-012) --- */

/**
 * `409` เมื่อชื่อแผนกนั้นมีคนใช้แล้ว (`uq_department_department_name`)
 *
 * เป็นคนละเรื่องกับ `422` ทุกตัวในไฟล์นี้: ไม่ใช่กฎทางธุรกิจที่ต้องแก้เงื่อนไขก่อน
 * แต่คือ "ของสิ่งนี้มีอยู่แล้ว" ซึ่งทางแก้คือเลือกชื่ออื่นหรือใช้แผนกที่มีอยู่
 */
export const DEPARTMENT_NAME_TAKEN = 'DEPARTMENT_NAME_TAKEN';

/**
 * `409` เมื่อรหัสพนักงานนั้นเป็นของคนอื่นไปแล้ว
 *
 * `employee_id` เป็น business key ที่ผู้ใช้กำหนดเอง (D-039) ไม่ใช่ค่าที่ระบบสร้าง
 * — การชนกันจึงเกิดได้จริงและบ่อย และข้อความของมันต้องบอกว่ารหัสนั้น **มีเจ้าของ
 * อยู่แล้ว** ไม่ใช่ "บันทึกไม่สำเร็จ" ซึ่งจะชวนให้กดซ้ำ
 */
export const EMPLOYEE_ID_TAKEN = 'EMPLOYEE_ID_TAKEN';

/** Body ของ `POST /v1/admin/departments` — คีย์เดียว (`extra = "forbid"`) */
export interface CreateDepartmentBody {
  readonly department_name: string;
}

/**
 * Body ของ `POST /v1/admin/employees` — สองคีย์บังคับ + `status` ที่ไม่บังคับ
 *
 * `status` เป็น optional จริงตาม contract (เซิร์ฟเวอร์เติม `ACTIVE` ให้) จึงประกาศ
 * เป็น optional ตรง ๆ แทนที่จะบังคับให้หน้าจอส่งค่าเสมอ — คีย์ที่หน้าจอเติมเองย่อม
 * กลบค่าเริ่มต้นของ contract ในวันที่ค่าเริ่มต้นนั้นเปลี่ยน
 *
 * **ไม่มีรหัสผ่านและไม่มี username ที่นี่โดยโครงสร้าง**: การสร้าง employee master
 * กับการออก credential เป็นคนละเรื่องและคนละเส้นทาง (`employee_credential`)
 */
export interface CreateEmployeeBody {
  readonly employee_id: string;
  readonly display_name: string;
  readonly status?: string;
}

/* ------------------------- แผนก/โรงงานของพนักงาน (WO-P1-M2-013) --- */

/**
 * `422` เมื่อ `department_id` ที่ส่งไปไม่มีแผนกนั้นอยู่จริง
 *
 * เป็น `422` ไม่ใช่ `404` โดยเจตนาของ contract เพราะแผนกเป็น **field ของคำขอ**
 * ส่วน `404` ของเส้นทางนี้สงวนไว้ให้ **พนักงานใน path** ที่ไม่มีอยู่ — สองกรณีนี้
 * ต้องอ่านออกว่าเป็นคนละอย่าง ไม่งั้นผู้ดูแลจะไปแก้ผิดฝั่ง
 */
export const DEPARTMENT_NOT_FOUND = 'DEPARTMENT_NOT_FOUND';

/** `422` เมื่อ `plant_code` ที่ส่งไปไม่ตรงกับโรงงานใดเลย */
export const PLANT_NOT_FOUND = 'PLANT_NOT_FOUND';

/**
 * กฎทุกข้อที่ contract เขียนไว้ตรง ๆ สำหรับสี่ endpoint ที่ `WO-P1-M10-018` ใช้
 *
 * **ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุ** ด้วยเหตุผลเดียวกับ
 * `PERMISSION_MATRIX_RULES` และ `SYSTEM_ROLE_RULES`
 */
export const ORG_ADMIN_RULES = [
  DEPARTMENT_NAME_TAKEN,
  EMPLOYEE_ID_TAKEN,
  DEPARTMENT_NOT_FOUND,
  PLANT_NOT_FOUND,
] as const;

export type OrgAdminRule = (typeof ORG_ADMIN_RULES)[number];

/**
 * Body ของ `POST /v1/admin/employees/{employee_id}/departments`
 *
 * `is_primary` **ไม่มีค่าเริ่มต้น** ด้วยเหตุผลเดียวกับที่ `granted` ไม่มี: ธงนี้เป็น
 * **ตำแหน่ง ไม่ใช่คุณสมบัติ** — ตั้งเป็น `true` แปลว่า "ย้ายแผนกหลักมาที่นี่" ซึ่ง
 * ปลดธงของแผนกอื่นไปด้วยในธุรกรรมเดียว body ที่ลืมระบุจะถูกเดาไปทางใดทางหนึ่ง และ
 * หนึ่งในสองทางนั้นคือการย้ายแผนกที่ตัดสินว่าคนนั้นทำอะไรได้
 */
export interface SetEmployeeDepartmentBody {
  readonly department_id: string;
  readonly is_primary: boolean;
  readonly granted: boolean;
}

/** หนึ่งแผนกที่พนักงานคนนั้นสังกัดอยู่ — ธง `is_primary` มาจากเซิร์ฟเวอร์เท่านั้น */
export interface EmployeeDepartmentLink {
  readonly department_id: string;
  readonly department_name: string;
  readonly is_primary: boolean;
}

/**
 * คำตอบของ `POST .../departments` — **สถานะหลังเปลี่ยนทั้งชุด ไม่ใช่ delta**
 *
 * นี่คือสิ่งเดียวที่หน้าจอเชื่อได้ว่า "ตอนนี้คนนี้สังกัดแผนกใดบ้าง และแผนกไหนเป็น
 * แผนกหลัก" การเดาจากปุ่มที่เพิ่งกด (ติ๊กธงให้แถวใหม่โดยไม่แตะแถวเดิม) จะแสดง
 * **สอง primary พร้อมกัน** ซึ่งเป็นสถานะที่ฐานข้อมูลไม่มีวันมี (partial unique index)
 * และจะทำให้ผู้ดูแลเชื่อว่าตัวเองเพิ่งสร้างมันขึ้นมา
 */
export interface EmployeeDepartments {
  readonly employee_id: string;
  readonly departments: readonly EmployeeDepartmentLink[];
}

/**
 * Body ของ `POST /v1/admin/employees/{employee_id}/plant-access`
 *
 * `plant_code` เป็น **business code** (`BKK-01`) ไม่ใช่ `plant_id` — เซิร์ฟเวอร์เป็น
 * ผู้แปลง และตอบ `422 PLANT_NOT_FOUND` เมื่อไม่มีโรงงานนั้น
 */
export interface SetEmployeePlantAccessBody {
  readonly plant_code: string;
  readonly is_home_plant: boolean;
  readonly granted: boolean;
}

/** หนึ่งโรงงานที่พนักงานคนนั้นเข้าถึงได้ — `is_home_plant` เป็น singleton เช่นกัน */
export interface EmployeePlantGrant {
  readonly plant_code: string;
  readonly is_home_plant: boolean;
}

/**
 * คำตอบของ `POST .../plant-access` — สถานะหลังเปลี่ยนทั้งชุด
 *
 * **นี่คือสิ่งที่กำหนด plant scope ของพนักงาน (D-004)**: ชุดนี้คือโรงงานทั้งหมดที่
 * คนนั้นมองเห็นข้อมูลธุรกิจได้ ตั้งแต่วินาทีที่คำขอนี้ตอบกลับ
 */
export interface EmployeePlantAccess {
  readonly employee_id: string;
  readonly plant_access: readonly EmployeePlantGrant[];
}

/* ------------------------------------------- SSO admin (WO-P2-M13-006b) --- */

export const ADMIN_OAUTH_CLIENTS_PATH_SEGMENT = 'oauth-clients';
export const MFA_RESET_NOT_ALLOWED_FOR_SELF = 'MFA_RESET_NOT_ALLOWED_FOR_SELF';
export const MFA_RESET_NOT_ALLOWED_FOR_PRIVILEGED_TARGET =
  'MFA_RESET_NOT_ALLOWED_FOR_PRIVILEGED_TARGET';

export type RedirectUriKind = 'LOGIN_CALLBACK' | 'POST_LOGOUT';
export const REDIRECT_URI_KINDS: readonly RedirectUriKind[] = ['LOGIN_CALLBACK', 'POST_LOGOUT'];

export interface OauthClientRedirectUri {
  readonly uri: string;
  readonly kind: RedirectUriKind;
}

/** หนึ่ง client จาก `GET /admin/oauth-clients` — ไม่มี secret/hash ในรูปนี้ */
export interface OauthClient {
  readonly client_id: string;
  readonly app_code: string;
  readonly client_name: string;
  readonly active: boolean;
  readonly require_app_role: boolean;
  readonly access_token_ttl_seconds: number;
  readonly id_token_ttl_seconds: number;
  readonly refresh_enabled: boolean;
  readonly refresh_token_absolute_ttl_seconds: number | null;
  readonly refresh_token_idle_ttl_seconds: number | null;
  readonly redirect_uris: readonly OauthClientRedirectUri[];
  /** role_code ของ app ของ client นี้ที่บังคับ MFA (สมมติฐาน: array ของสตริง) */
  readonly mfa_roles: readonly string[];
  readonly secret_rotated_at: string | null;
}

export interface OauthClientListPage {
  readonly items: readonly OauthClient[];
  readonly next_cursor: string | null;
}

/** คำตอบของสร้าง/หมุน secret — `client_secret` แสดงครั้งเดียวแล้วต้องไม่ถูกเก็บ */
export interface OauthClientWithSecret {
  readonly client: OauthClient;
  readonly client_secret: string;
}

export interface CreateOauthClientBody {
  readonly client_id: string;
  readonly app_code: string;
  readonly client_name: string;
  readonly redirect_uris: readonly OauthClientRedirectUri[];
  readonly mfa_roles: readonly string[];
  readonly access_token_ttl_seconds: number;
  readonly id_token_ttl_seconds: number;
  readonly refresh_enabled: boolean;
  readonly refresh_token_absolute_ttl_seconds: number | null;
  readonly refresh_token_idle_ttl_seconds: number | null;
  readonly require_app_role: boolean;
}

export type UpdateOauthClientBody = Omit<CreateOauthClientBody, 'client_id' | 'app_code' | 'client_name'> & {
  readonly active: boolean;
};

/** หนึ่งแถวของแคตตาล็อก `GET /admin/app-roles` */
export interface AppRoleCatalogEntry {
  readonly app_code: string;
  readonly app_name: string | null;
  /** `false` = the app is switched off; its roles are not offered for new grants */
  readonly app_active: boolean;
  readonly role_code: string;
  readonly description: string;
  readonly active: boolean;
}

export interface EmployeeAppRole {
  readonly app_code: string;
  readonly role_code: string;
  readonly granted_by_employee_id: string | null;
  readonly granted_at: string | null;
  readonly self_granted: boolean;
}

export interface MfaResetOutcome {
  readonly employee_id: string;
  readonly status: string;
  readonly revoked_session_count: number;
  readonly revoked_idp_session_count: number;
  readonly revoked_grant_count: number;
}

/** คำตอบของ `GET .../app-roles` — สมมติฐาน WO-P2-M13-006b: `{ employee_id, app_roles }` */
export interface EmployeeAppRoles {
  readonly employee_id: string;
  readonly app_roles: readonly EmployeeAppRole[];
}

export interface AppRoleCatalogPage {
  readonly items: readonly AppRoleCatalogEntry[];
  readonly next_cursor: string | null;
}

export interface SetEmployeeAppRoleBody {
  readonly role_code: string;
}

export interface RevokeEmployeeAppRoleBody {
  readonly app_code: string;
}
